// What an hour of a partner's work costs on this project, and the people
// whose own rate overrides it. The cost of hours spent on issues comes
// from these (back/utils/issueHours.rateFor). Owner / company admins only:
// the page shows the button when the row says canSeeRates, and the API
// refuses anyone else.
import React, { useEffect, useState } from "react";
import { Modal, InputNumber, Table, message } from "antd";
import { setParticipantRates } from "api/participants/participantsApi";

const rateInput = (value, onChange, placeholder) => (
  <InputNumber min={0} max={100000} step={5} precision={2} value={value} onChange={onChange}
    placeholder={placeholder} addonBefore="$" addonAfter="/h" style={{ width: 190 }} />
);

export default function ParticipantRatesModal({ open, projectId, row, onClose, onSaved }) {
  const [company, setCompany] = useState(null);
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    setCompany(row.hourlyRate ?? null);
    setPeople([
      ...(row.supervisors || []).map((s) => ({ key: `s:${s.email}`, list: "supervisors", email: s.email, name: s.name, role: "Supervisor", hourlyRate: s.hourlyRate ?? null })),
      ...(row.users || []).map((u) => ({ key: `u:${u.email}`, list: "users", email: u.email, name: u.name, role: u.role || "Team", hourlyRate: u.hourlyRate ?? null })),
    ]);
  }, [open, row]);

  const setPerson = (key, v) => setPeople((list) => list.map((p) => (p.key === key ? { ...p, hourlyRate: v } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const pick = (list) => people.filter((p) => p.list === list).map((p) => ({ email: p.email, hourlyRate: p.hourlyRate ?? null }));
      const saved = await setParticipantRates(projectId, row._id, { hourlyRate: company ?? null, users: pick("users"), supervisors: pick("supervisors") });
      message.success(`Rates saved for ${row.companyName || "the partner"}`);
      onSaved && onSaved(saved);
      onClose && onClose();
    } catch (e) {
      message.error(e.message || "Could not save the rates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Hourly rates - ${row ? row.companyName || "partner" : ""}`}
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Save rates"
      confirmLoading={saving}
      width={640}
      destroyOnClose
    >
      <div style={{ fontSize: 12.5, color: "#555", marginBottom: 12 }}>
        The cost of the hours this company's people submit with their resolutions. A person with their own rate uses it;
        everyone else uses the company rate. Leave a rate empty to use the company rate. Visible to the project owner and company admins only.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <b style={{ width: 120 }}>Company rate</b>
        {rateInput(company, setCompany, "not set")}
      </div>
      <Table
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={people}
        locale={{ emptyText: "No supervisors or team members on this partner yet" }}
        columns={[
          { title: "Person", key: "who", render: (_, p) => <><div>{p.name}</div><div style={{ fontSize: 11.5, color: "#888" }}>{p.email}</div></> },
          { title: "Role", dataIndex: "role", width: 110 },
          { title: "Own rate", key: "rate", width: 210, render: (_, p) => rateInput(p.hourlyRate, (v) => setPerson(p.key, v), company != null ? `company: $${company}` : "company rate") },
        ]}
      />
    </Modal>
  );
}
